// ContributionHistory
/* eslint-disable @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';

import { getEmployeeContribution } from 'service';
import type { IEmployeeContribution } from 'types/pages';
import { CustomDataGrid } from 'components';
import { strings } from 'i18n/Strings';
import { colorTheme } from '../../../theme';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';

type GridRowData = Record<string, unknown>;

const ContributionHistory = () => {
  const [contributions, setContributionInfo] = useState<
    IEmployeeContribution[] | unknown
  >([]);
  const currentTheme = useTheme();
  const colors = colorTheme(currentTheme.palette.mode);
  const { userId } = useParams();

  useEffect(() => {
    if (userId) {
      getContributionData();
    }
  }, [userId]);

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'contributionPeriod',
      flex: 0.5,
      cellClassName: 'month-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.contributionPeriod}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.monthYear} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row?.contributionFile?.period}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'employeeContribution',
      flex: 0.5,
      align: 'right',
      headerAlign: 'right',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employeeContribution}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.employeeContribution} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(row.employeeContribution).toFixed(2))}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'employerContribution',
      flex: 0.5,
      align: 'right',
      headerAlign: 'right',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employerContribution}
        </Typography>
      ),
      renderCell: ({ row }) => {
        return (
          <Tooltip title={row.employerContribution} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {CurrencyFormater(Number(row.employerContribution).toFixed(2))}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'totalContribution',
      flex: 0.5,
      align: 'right',
      headerAlign: 'right',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.totalContribution}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.totalContribution} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(row.totalContribution).toFixed(2))}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  const getContributionData = () => {
    getEmployeeContribution(userId as string)
      .then((response: any) => {
        setContributionInfo(response.basicInfo);
      })
      .catch(() => {});
  };

  return (
    <Box>
      <CustomDataGrid
        title=""
        columns={columns}
        rows={contributions as GridRowData[]}
        getRowId={(row: GridRowData) => `${String(row.id)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="contribution history"
      />
    </Box>
  );
};

export default ContributionHistory;
